// Production workflow status for a marketing plan.
//
// PROVISIONAL. The backend has not defined GET /marketing-plans/:id/production
// yet. These types and the reader follow the spec: a ProductionJob has
// revision, workflowVersion, colorway, creativeType, required,
// existingQaPassed, toMake, stage, attempts, estimated and actual cost,
// and resulting creative IDs. Plan coverage is how much of that gap is
// filled. Product ids, plan ids, and creative ids are not kept on the
// view. Adjust this file when the backend contract lands.
//
// Spec path: GET /marketing-plans/:id/production
// This app calls GET /api/marketing-plans/:planId/production?brandId=&revision=
//
// Status renders only when VITE_PLAN_PRODUCTION=true. This module only
// reads. It does not estimate or start a production run.

import { apiJson } from '../auth/apiFetch';
import { formatMoney, isMissingEndpoint, isSecretText, safeText } from './client';

/** Provisional view of one coverage rollup line. */
export type ProductionCoverageLine = {
  text: string;
};

/** Provisional view of one production job. Creative ids are a count only. */
export type ProductionJobView = {
  label: string;
  stage: string | null;
  revision: string | null;
  workflowVersion: string | null;
  attempt: string | null;
  estimate: string | null;
  cap: string | null;
  actual: string | null;
  creativeCount: number | null;
};

/** Provisional production payload rendered on the plan detail. */
export type PlanProduction = {
  workflowVersion: string | null;
  revision: string | null;
  unavailableReason: string | null;
  coverage: ProductionCoverageLine[];
  jobs: ProductionJobView[];
};

export type PlanProductionResult = {
  production: PlanProduction | null;
};

type Rec = Record<string, unknown>;

export function planProductionEnabled(env: { VITE_PLAN_PRODUCTION?: string } = import.meta.env): boolean {
  return env.VITE_PLAN_PRODUCTION === 'true';
}

export function productionUrl(brandId: string, planId: string, revision?: string | null): string {
  const params = new URLSearchParams({ brandId });
  if (revision) params.set('revision', revision);
  return `/api/marketing-plans/${encodeURIComponent(planId)}/production?${params}`;
}

export async function getPlanProduction(
  brandId: string,
  planId: string,
  revision?: string | null
): Promise<PlanProductionResult> {
  try {
    const body = await apiJson<unknown>(productionUrl(brandId, planId, revision), { cache: 'no-store' });
    return { production: readPlanProduction(body) };
  } catch (err: unknown) {
    if (isMissingEndpoint(err)) return { production: null };
    return {
      production: {
        workflowVersion: null,
        revision: null,
        unavailableReason: "Production status isn't available yet.",
        coverage: [],
        jobs: []
      }
    };
  }
}

export function readPlanProduction(body: unknown): PlanProduction | null {
  const payload = payloadOf(body);
  if (!payload) return null;
  const currency = currencyCode(payload.currency);
  const coverageRows = records(payload.coverage ?? payload.gaps ?? payload.planCoverage, true);
  const jobRows = records(payload.jobs ?? payload.productionJobs);
  const coverage = unique(coverageRows.map((row) => coverageText(row)).filter((line): line is string => Boolean(line)));
  const fromJobs = coverage.length ? [] : unique(jobRows.map((row) => coverageText(row)).filter((line): line is string => Boolean(line)));
  const jobs = jobRows.map((row) => readJob(row, currency)).filter((job): job is ProductionJobView => job != null);
  const production: PlanProduction = {
    workflowVersion: versionText(payload.workflowVersion ?? payload.version),
    revision: revisionText(payload.revision),
    unavailableReason: prose(payload.unavailableReason, 400),
    coverage: coverage.length ? coverage.map((text) => ({ text })) : fromJobs.map((text) => ({ text })),
    jobs
  };
  if (!production.unavailableReason && !production.workflowVersion && !production.revision && production.coverage.length === 0 && production.jobs.length === 0) {
    return null;
  }
  return production;
}

function readJob(row: Rec, currency: string | null): ProductionJobView | null {
  const title = prose(row.title ?? row.jobTitle, 160);
  const product = productLabel(row.product) ?? prose(row.productName ?? row.name, 120);
  const colorway = prose(row.colorway ?? row.color, 80);
  const creativeType = creativeLabel(row.creativeType ?? row.type);
  const stage = stageLabel(row.stage ?? row.status);
  const parts = unique([title, product, colorway, creativeType].filter((part): part is string => Boolean(part)));
  const creativeCount = creativeCountOf(row);
  const estimate = jobMoney(row, ['estimatedCost', 'estimated', 'estimate'], ['estimatedCostUsd', 'estimateUsd'], currency);
  const cap = jobMoney(row, ['cap'], ['capUsd'], currency);
  const actual = jobMoney(row, ['actualCost', 'actual'], ['actualCostUsd', 'actualUsd'], currency);
  const attempt = attemptText(row.attempts ?? row.attempt);
  if (!parts.length && !stage && !attempt && !estimate && !cap && !actual && !(creativeCount != null && creativeCount > 0)) return null;
  return {
    label: parts.length ? parts.join(' · ') : 'Production job',
    stage,
    revision: revisionText(row.revision),
    workflowVersion: versionText(row.workflowVersion ?? row.version),
    attempt,
    estimate,
    cap,
    actual,
    creativeCount
  };
}

function coverageText(row: Rec): string | null {
  const required = count(row.required);
  const passed = count(row.existingQaPassed ?? row.qaPassed ?? row.filled);
  const toMake = count(row.toMake ?? row.remaining ?? row.missing);
  const counts = [
    required == null ? null : `${formatCount(required)} required`,
    passed == null ? null : `${formatCount(passed)} QA-passed`,
    toMake == null ? null : `${formatCount(toMake)} still to make`
  ].filter((part): part is string => Boolean(part));
  if (!counts.length) return prose(row.text ?? row.summary, 240);
  const label = unique([
    prose(row.line, 120),
    productLabel(row.product) ?? prose(row.productName ?? row.subject ?? row.name, 120),
    prose(row.colorway ?? row.color, 80),
    creativeLabel(row.creativeType ?? row.type)
  ].filter((part): part is string => Boolean(part))).join(' · ');
  return prose(label ? `${label}: ${counts.join(', ')}` : counts.join(', '), 300);
}

function creativeCountOf(row: Rec): number | null {
  const listed = row.creativeIds ?? row.resultingCreativeIds ?? row.resultingCreatives ?? row.creatives;
  if (Array.isArray(listed)) return listed.length;
  return count(row.creativeCount ?? row.resultingCount);
}

function stageLabel(value: unknown): string | null {
  const raw = prose(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  const labels: Record<string, string> = {
    generate: 'Generate',
    generating: 'Generating',
    qa: 'QA',
    'quality check': 'QA',
    copy: 'Copy',
    approve: 'Approve',
    approval: 'Approve',
    assemble: 'Assemble ads',
    'assemble ads': 'Assemble ads',
    review: 'Review',
    done: 'Done',
    complete: 'Done',
    completed: 'Done',
    failed: 'Failed',
    queued: 'Queued',
    pending: 'Queued'
  };
  return labels[key] ?? raw;
}

function creativeLabel(value: unknown): string | null {
  if (typeof value === 'string' && isIdToken(value.trim())) return null;
  const raw = prose(value, 40);
  if (!raw || isIdToken(raw)) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'catalog image' || key === 'lifestyle') return 'catalog image';
  if (key === 'catalog video') return 'catalog video';
  if (key === 'static ad' || key === 'static') return 'static ad';
  if (key === 'video ad' || key === 'video') return 'video ad';
  return raw;
}

function attemptText(value: unknown): string | null {
  if (isRec(value)) {
    const used = count(value.count ?? value.attempt ?? value.used);
    const limit = count(value.limit ?? value.max);
    if (used == null) return null;
    return limit == null ? `Attempt ${formatCount(used)}` : `Attempt ${formatCount(used)} of ${formatCount(limit)}`;
  }
  const attempts = count(value);
  if (attempts == null) return null;
  return `Attempt ${formatCount(attempts)}`;
}

function jobMoney(row: Rec, keys: string[], usdKeys: string[], currency: string | null): string | null {
  for (const key of keys) {
    if (row[key] == null) continue;
    const formatted = money(row[key], currency);
    if (formatted) return formatted;
  }
  for (const key of usdKeys) {
    if (row[key] == null) continue;
    const formatted = money(row[key], currency ?? 'USD');
    if (formatted) return formatted;
  }
  return null;
}

function money(value: unknown, currency: string | null): string | null {
  if (isRec(value)) {
    const nested = currencyCode(value.currency) ?? currency;
    const amount = num(value.amount ?? value.value ?? value.usd);
    if (amount == null) return null;
    return formatMoney(amount, nested);
  }
  const amount = num(value);
  if (amount == null) return null;
  return formatMoney(amount, currency);
}

function versionText(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(Math.trunc(value));
  return prose(value, 40);
}

function revisionText(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(Math.trunc(value));
  return prose(value, 40);
}

function payloadOf(body: unknown): Rec | null {
  if (!isRec(body)) return null;
  const nested = isRec(body.production) ? body.production : null;
  if (!nested) return body;
  const topHas = ['jobs', 'coverage', 'unavailableReason', 'workflowVersion'].some((key) => body[key] != null);
  return topHas ? body : nested;
}

function productLabel(value: unknown): string | null {
  if (isRec(value)) return prose(value.title ?? value.name, 120);
  return prose(value, 120);
}

function prose(value: unknown, limit: number): string | null {
  const text = typeof value === 'string' ? value.trim() : null;
  if (!text) return safeText(value, limit);
  const cleaned = text
    .replace(/\b[a-f0-9]{24}\b/gi, ' ')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, ' ')
    .replace(/\bcreative_[A-Za-z0-9_-]+\b/gi, ' ')
    .replace(/EAA[A-Za-z0-9]{10,}/g, ' ')
    .replace(/\bact_\d{4,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned || cleaned.length > limit || isSecretText(cleaned) || looksLikePii(cleaned)) return null;
  return cleaned;
}

function isIdToken(text: string): boolean {
  return /^[a-f0-9]{24}$/i.test(text)
    || /^act_\d{4,}$/.test(text)
    || /^EAA[A-Za-z0-9]{10,}$/.test(text)
    || /^creative_[A-Za-z0-9_-]+$/i.test(text)
    || /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(text);
}

function looksLikePii(text: string): boolean {
  if (/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/.test(text)) return true;
  if (/\b\d{3}[-.\s]\d{3}[-.\s]\d{4}\b/.test(text)) return true;
  return false;
}

function currencyCode(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!/^[A-Za-z]{3}$/.test(text)) return null;
  return text.toUpperCase();
}

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function count(value: unknown): number | null {
  const parsed = num(value);
  if (parsed == null || parsed < 0) return null;
  return Math.trunc(parsed);
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function isRec(value: unknown): value is Rec {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function records(value: unknown, allowSingle = false): Rec[] {
  if (Array.isArray(value)) return value.filter(isRec);
  if (allowSingle && isRec(value)) return [value];
  return [];
}
